/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @version 1.10.0
 * @since 1.0.0
 */
import {
    forwardRef,
    Component,
    ComponentClass,
    ComponentType,
    createRef,
    PropsWithChildren,
} from 'react';

import get from 'lodash/get';
import invoke from 'lodash/invoke';

/**
 * @description Describe interface props for withWindowEvents HOC
 * @interface IWindowEvent
 *
 * @property {keyof WindowEventMap} event Define event name.
 * @property {string} handler Define handler for event
 * @see withWindowEvents HOC
 */
export interface IWindowEvent {
    event: keyof WindowEventMap;
    handler: string;
    options?: object;
}

/**
 * @description HOC to add window events like 'click', 'scroll' to Component
 * @param {React.ComponentType<P>} WrapperComponent
 * @param {IWindowEvent[]} events
 * @return {React.ComponentType<P>}
 */
function withWindowEvents<P>(WrapperComponent: ComponentType<P>, events: IWindowEvent[]): ComponentClass<P> {
    class WithWindowEvents extends Component<P> {

        // @ts-ignore
        componentRef = this.props.forwardedRef || createRef<P>();

        componentDidMount() {
            // Add events listener
            if (this.componentRef.current) {
                invoke(this.componentRef, 'current.componentDidMount');
                events.forEach(item =>
                    window.addEventListener(
                        item.event,
                        get(this.componentRef.current, item.handler),
                        item.options || true,
                    ));
            }
        }

        componentWillUnmount() {
            // Remove events listener
            if (this.componentRef.current) {
                invoke(this.componentRef, 'current.componentWillUnmount');
                events.forEach(item =>
                    window.removeEventListener(
                        item.event,
                        get(this.componentRef.current, item.handler),
                        true,
                    ));
            }
        }

        render() {
            return (<WrapperComponent ref={this.componentRef} {...this.props}/>);
        }

    }

    // @ts-ignore
    return forwardRef((props: PropsWithChildren<P>, ref: any) =>
        <WithWindowEvents {...props} forwardedRef={ref} />,
    );
}

export default withWindowEvents;
